import React, { useState, useEffect } from 'react';
import {
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  ChatBubbleBottomCenterTextIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  AcademicCapIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  StudentSession,
  WeeklyQuery,
  WeeklyQueryCategory,
} from '../../types';
import {
  WEEKLY_QUERY_CATEGORIES,
  isMonday,
  getWeekIdentifier,
  getWeekMondayDate,
  getNextMondayDate,
  getTargetClassWeekLabel,
  autoCategorizeQuery,
  submitWeeklyStudentQuery,
  subscribeToStudentWeeklyQueries,
  formatMondayDisplayName,
  isQueryInMondayWeek,
} from '../../services/firebaseService';

interface WeeklyQueriesStudentTabProps {
  session: StudentSession;
}

const WeeklyQueriesStudentTab: React.FC<WeeklyQueriesStudentTabProps> = ({ session }) => {
  const [queries, setQueries] = useState<WeeklyQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form states
  const [questionText, setQuestionText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<WeeklyQueryCategory | 'auto'>('auto');
  const [showPastWeeks, setShowPastWeeks] = useState(false);

  // Demo / evaluation mode toggle to allow testing on any day
  const [demoMode, setDemoMode] = useState<boolean>(() => {
    return localStorage.getItem('kreed_query_demo_mode') === 'true';
  });

  const isTodayMonday = isMonday();
  const isSubmissionOpen = isTodayMonday || demoMode;
  const currentWeekId = getWeekIdentifier();
  const nextMonday = getNextMondayDate();

  // Subscribe to student's queries
  useEffect(() => {
    if (!session?.regNoSuffix) return;
    setLoading(true);

    const unsubscribe = subscribeToStudentWeeklyQueries(
      session.regNoSuffix,
      (list) => {
        setQueries(list);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [session?.regNoSuffix]);

  // Split into this week vs previous weeks
  const thisWeekQueries = queries.filter((q) => isQueryInMondayWeek(q, currentWeekId));
  const pastWeekQueries = queries.filter((q) => !isQueryInMondayWeek(q, currentWeekId));
  const questionsUsedCount = thisWeekQueries.length;
  const remainingAllowance = Math.max(0, 3 - questionsUsedCount);

  // Live auto-detected category preview
  const liveAuto = questionText.trim().length >= 5 ? autoCategorizeQuery(questionText) : null;

  const handleToggleDemoMode = () => {
    const nextVal = !demoMode;
    setDemoMode(nextVal);
    localStorage.setItem('kreed_query_demo_mode', String(nextVal));
  };

  const handleQuickPromptClick = (prompt: string, category?: WeeklyQueryCategory) => {
    setQuestionText(prompt);
    if (category) {
      setSelectedCategory(category);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!questionText.trim() || questionText.trim().length < 5) {
      setErrorMessage('Please type a question of at least 5 characters.');
      return;
    }
    if (questionsUsedCount >= 3) {
      setErrorMessage('You have already submitted the maximum of 3 questions for this week.');
      return;
    }

    setSubmitting(true);
    try {
      const catOverride = selectedCategory === 'auto' ? undefined : selectedCategory;
      await submitWeeklyStudentQuery(
        session,
        questionText.trim(),
        catOverride,
        demoMode // Allows test bypass if demo mode enabled
      );

      setSuccessMessage('Your question was submitted successfully! It has been scheduled for upcoming classes.');
      setQuestionText('');
      setSelectedCategory('auto');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to submit question. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Hero / Quota Overview Card */}
      <div className="bg-gradient-to-br from-indigo-950/80 via-primary-950/90 to-slate-900 border border-indigo-500/30 rounded-3xl p-5 sm:p-7 shadow-2xl relative overflow-hidden backdrop-blur-xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center space-x-2.5">
              <span className={`px-2.5 py-1 rounded-full text-[11px] tracking-wider uppercase flex items-center space-x-1.5 ${
                isTodayMonday
                  ? 'bg-warning-500 text-slate-950 shadow-md font-black animate-pulse'
                  : 'bg-warning-500/20 text-warning-300 border border-warning-500/30 font-bold'
              }`}>
                <SparklesIcon className="w-3.5 h-3.5" />
                <span>{isTodayMonday ? 'Monday Special Question Session' : 'Weekly Doubt Resolution'}</span>
              </span>
              <span className="text-xs text-blue-200">
                Week of {getWeekMondayDate()}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              {isTodayMonday 
                ? 'Monday Special Session: Submit Your Doubts & Queries'
                : "Ask Your Questions for Next Week's Classes"}
            </h2>
            <p className="text-xs sm:text-sm text-blue-100/90 leading-relaxed">
              {isTodayMonday ? (
                <>
                  Today is <strong>Monday</strong>! The special question &amp; doubt clearing portal is <strong>OPEN TODAY</strong>. Submit up to <strong>3 priority questions</strong> below for instructors to address directly during today's live lecture and doubt clearing sessions!
                </>
              ) : (
                <>
                  Every enrolled candidate can ask <strong>up to 3 questions each week</strong>. The question portal opens on <strong>Mondays</strong>. Questions are automatically sorted and assigned to instructors, who address them directly in upcoming live lectures!
                </>
              )}
            </p>
          </div>

          {/* Weekly Allowance Tracker Card */}
          <div className="bg-white/10 border border-white/20 rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center min-w-[240px] shadow-lg">
            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-200">
              Your Weekly Question Quota
            </span>
            <div className="my-2 flex items-baseline space-x-1.5">
              <span className="text-4xl font-extrabold text-white">
                {questionsUsedCount}
              </span>
              <span className="text-xl text-blue-300 font-bold">/ 3</span>
            </div>
            <p className="text-xs text-blue-100">
              {remainingAllowance > 0
                ? `${remainingAllowance} question${remainingAllowance > 1 ? 's' : ''} remaining this week`
                : 'All 3 weekly questions submitted!'}
            </p>

            {/* 3 Progress Dots */}
            <div className="grid grid-cols-3 gap-2 w-full mt-3 pt-3 border-t border-white/15">
              {[1, 2, 3].map((num) => {
                const isUsed = questionsUsedCount >= num;
                return (
                  <div
                    key={num}
                    className={`py-1 rounded-lg text-[10px] font-extrabold flex items-center justify-center space-x-1 border ${
                      isUsed
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-sm'
                        : 'bg-white/5 text-blue-300/60 border-white/10'
                    }`}
                  >
                    <span>Q{num}</span>
                    {isUsed ? (
                      <CheckCircleIcon className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-300/40" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Portal Status Bar & Evaluation Mode Switch */}
        <div className="mt-5 pt-4 border-t border-white/15 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center space-x-2">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                isSubmissionOpen ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
              }`}
            />
            {isTodayMonday ? (
              <span className="text-emerald-300 font-semibold">
                🟢 Monday Portal is OPEN — Submit your questions today for next week's classes!
              </span>
            ) : isSubmissionOpen ? (
              <span className="text-warning-300 font-semibold flex items-center space-x-1">
                <span>⚡ Evaluation Mode: Portal unlocked for testing (Simulating Monday Window)</span>
              </span>
            ) : (
              <span className="text-amber-200">
                Portal opens on <strong>Mondays</strong>. Next window:{' '}
                <strong>
                  {nextMonday.toLocaleDateString('en-IN', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </strong>
              </span>
            )}
          </div>

          {/* Test / Evaluation Mode Toggle */}
          <div className="flex items-center space-x-2 bg-white/10 px-3 py-1 rounded-full border border-white/20">
            <label className="text-[11px] text-blue-200 cursor-pointer select-none">
              Evaluation / Demo Mode
            </label>
            <input
              type="checkbox"
              checked={demoMode}
              onChange={handleToggleDemoMode}
              className="rounded text-indigo-500 focus:ring-indigo-400 h-3.5 w-3.5 cursor-pointer"
              title="Unlock submission for testing on any day"
            />
          </div>
        </div>
      </div>

      {/* Question Submission Form Section */}
      {remainingAllowance > 0 && isSubmissionOpen ? (
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 sm:p-7 shadow-xl">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-white/15">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 flex items-center justify-center">
                <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-white">
                  Ask Question #{questionsUsedCount + 1}
                </h3>
                <p className="text-xs text-blue-200">
                  Targeted for: <strong className="text-white">{getTargetClassWeekLabel()}</strong>
                </p>
              </div>
            </div>

            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-200 border border-indigo-500/30">
              {remainingAllowance} Question{remainingAllowance > 1 ? 's' : ''} Left
            </span>
          </div>

          {errorMessage && (
            <div className="mb-5 p-3.5 rounded-2xl bg-red-950/60 border border-red-500/40 text-red-200 text-xs flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-5 p-3.5 rounded-2xl bg-emerald-950/60 border border-emerald-500/40 text-emerald-200 text-xs flex items-center space-x-2">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Category Selection */}
            <div>
              <label className="block text-xs font-bold text-blue-100 mb-2">
                Question Category / Subject Area
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('auto')}
                  className={`p-2.5 rounded-xl border text-left text-xs font-semibold transition-all ${
                    selectedCategory === 'auto'
                      ? 'bg-indigo-600 text-white border-indigo-400 shadow-md ring-2 ring-indigo-400/40'
                      : 'bg-white/10 text-blue-200 border-white/15 hover:bg-white/15'
                  }`}
                >
                  <div className="text-base mb-1">⚡</div>
                  <div className="truncate">Auto-Detect</div>
                  <div className="text-[10px] opacity-75 truncate">Smart AI Sort</div>
                </button>

                {WEEKLY_QUERY_CATEGORIES.map((cat) => {
                  const isSelected = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`p-2.5 rounded-xl border text-left text-xs font-semibold transition-all ${
                        isSelected
                          ? 'bg-indigo-600 text-white border-indigo-400 shadow-md ring-2 ring-indigo-400/40'
                          : 'bg-white/10 text-blue-200 border-white/15 hover:bg-white/15'
                      }`}
                    >
                      <div className="text-base mb-1">{cat.icon}</div>
                      <div className="truncate">{cat.label.split('&')[0]}</div>
                      <div className="text-[10px] opacity-75 truncate">{cat.id}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Question Textarea */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-blue-100">
                  Your Question Details
                </label>
                <span className={`text-[11px] font-mono ${
                  questionText.length > 550 ? 'text-amber-400' : 'text-blue-300'
                }`}>
                  {questionText.length} / 600 characters
                </span>
              </div>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value.slice(0, 600))}
                rows={4}
                placeholder="Be clear and specific. For example: 'What is the ideal pacing strategy for the 1600m run during the second half of the trial?' or 'How should I schedule protein and water intake before morning physical drill?'"
                className="w-full px-4 py-3 rounded-2xl bg-slate-900/80 border border-white/20 text-white text-xs sm:text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              />
            </div>

            {/* Smart Auto-Tag Preview */}
            {liveAuto && selectedCategory === 'auto' && (
              <div className="p-3 rounded-2xl bg-indigo-950/40 border border-indigo-500/30 flex items-center justify-between flex-wrap gap-2 text-xs">
                <div className="flex items-center space-x-2">
                  <SparklesIcon className="w-4 h-4 text-warning-400" />
                  <span className="text-blue-200">Auto-Tagged Topic:</span>
                  <span className="font-bold text-white bg-indigo-500/30 px-2 py-0.5 rounded-lg border border-indigo-400/30">
                    {liveAuto.categoryLabel}
                  </span>
                  <span className="text-indigo-300 text-[11px]">
                    &bull; Cluster: <em>{liveAuto.similarityCluster}</em>
                  </span>
                </div>
                <span className="text-[10px] text-blue-200/80 uppercase font-mono">
                  Priority: {liveAuto.priority}
                </span>
              </div>
            )}

            {/* Quick Inspiration Prompts */}
            <div>
              <span className="block text-[11px] font-semibold text-blue-200 mb-2">
                💡 Common Topics & Quick Prompts (Click to fill):
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleQuickPromptClick(
                    'What are the best warmup and breathing drills to avoid side-stitch during the 1600m physical test?',
                    'physical_training'
                  )}
                  className="px-2.5 py-1 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-[11px] text-blue-100 transition"
                >
                  🏃 1600m Breathing & Side-stitch
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickPromptClick(
                    'What should be the optimal pre-workout meal 2 hours before morning physical conditioning?',
                    'sports_nutrition'
                  )}
                  className="px-2.5 py-1 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-[11px] text-blue-100 transition"
                >
                  🥗 Pre-workout Meal Timing
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickPromptClick(
                    'Could the instructor explain the standard pull-up grip and chin clearance evaluation criteria?',
                    'rules_regulations'
                  )}
                  className="px-2.5 py-1 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-[11px] text-blue-100 transition"
                >
                  📋 Pull-up Grip & Criteria
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickPromptClick(
                    'Will there be practical video demonstrations for sprint starts and acceleration mechanics next week?',
                    'curriculum_classes'
                  )}
                  className="px-2.5 py-1 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-[11px] text-blue-100 transition"
                >
                  📚 Sprint Video Demos
                </button>
              </div>
            </div>

            {/* Submit Action Button */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-[11px] text-blue-200 flex items-center space-x-1">
                <ShieldCheckIcon className="w-4 h-4 text-emerald-400" />
                <span>Verified Enrolled Student: ID {session.regNoSuffix}</span>
              </span>

              <button
                type="submit"
                disabled={submitting || !questionText.trim()}
                className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 text-white font-bold text-xs sm:text-sm shadow-xl shadow-indigo-600/30 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer active:scale-95"
              >
                {submitting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Submitting Question #{questionsUsedCount + 1}...</span>
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-4 h-4" />
                    <span>Submit Question #{questionsUsedCount + 1} of 3</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : remainingAllowance === 0 ? (
        <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-3xl p-6 text-center text-white backdrop-blur-md shadow-xl space-y-2">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-2 border border-emerald-500/40">
            <CheckCircleIcon className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold">
            Weekly Questions Complete!
          </h3>
          <p className="text-xs text-emerald-200 max-w-md mx-auto">
            You have submitted all <strong>3 of 3</strong> permitted questions for this week. Your instructors will review them and cover them in upcoming classes next week.
          </p>
        </div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-700/60 rounded-3xl p-6 text-center text-white backdrop-blur-md shadow-xl space-y-3">
          <div className="w-12 h-12 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-2 border border-indigo-500/30">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <h3 className="text-base sm:text-lg font-bold">
            Question Submission Opens on Mondays
          </h3>
          <p className="text-xs text-blue-200 max-w-lg mx-auto leading-relaxed">
            The question clearing portal accepts submissions every <strong>Monday</strong> so instructors can review, cluster, and prepare curriculum for next week's classes.
          </p>
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/10 text-xs text-blue-100 border border-white/15">
            <ClockIcon className="w-4 h-4 text-warning-400" />
            <span>
              Next Monday: <strong>{nextMonday.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}</strong>
            </span>
          </div>
          <div>
            <button
              type="button"
              onClick={handleToggleDemoMode}
              className="text-xs text-indigo-300 underline hover:text-indigo-200 mt-2 cursor-pointer"
            >
              (Or click here to enable Evaluation Mode to submit a test question now)
            </button>
          </div>
        </div>
      )}

      {/* Student's Current Week Submissions */}
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 sm:p-7 shadow-xl">
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-white/15">
          <div className="flex items-center space-x-2.5">
            <QuestionMarkCircleIcon className="w-5 h-5 text-warning-400" />
            <h3 className="font-bold text-sm sm:text-base text-white">
              Your Questions Submitted This Week ({thisWeekQueries.length}/3)
            </h3>
          </div>
          <span className="text-xs text-blue-200">
            {getWeekMondayDate()}
          </span>
        </div>

        {loading ? (
          <div className="py-8 text-center text-xs text-blue-200 flex items-center justify-center space-x-2">
            <ArrowPathIcon className="w-4 h-4 animate-spin text-indigo-400" />
            <span>Loading your weekly questions...</span>
          </div>
        ) : thisWeekQueries.length === 0 ? (
          <div className="py-10 text-center text-xs text-blue-200 space-y-2">
            <p>You haven't submitted any questions for this week yet.</p>
            <p className="text-blue-300/70">
              Submit your question above to have the instructor address it during next week's lectures!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {thisWeekQueries.map((q, idx) => (
              <div
                key={q.id || idx}
                className="p-4 sm:p-5 rounded-2xl bg-white/10 border border-white/15 flex flex-col gap-3 shadow-md hover:bg-white/15 transition-all"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center space-x-2">
                    <span className="w-6 h-6 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/40 text-xs font-bold flex items-center justify-center">
                      Q{q.questionNumber || idx + 1}
                    </span>
                    <span className="text-xs font-bold text-white px-2.5 py-0.5 rounded-full bg-white/15 border border-white/20">
                      {q.categoryLabel || q.category}
                    </span>
                    {q.similarityCluster && (
                      <span className="text-[10px] text-blue-200 hidden sm:inline">
                        Topic: {q.similarityCluster}
                      </span>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div>
                    {q.status === 'planned_for_class' ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-blue-500/30 text-blue-200 border border-blue-400/40 shadow-sm flex items-center space-x-1">
                        <AcademicCapIcon className="w-3.5 h-3.5 text-blue-300" />
                        <span>Scheduled for Next Class!</span>
                      </span>
                    ) : q.status === 'addressed_in_class' ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 shadow-sm flex items-center space-x-1">
                        <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Addressed in Live Class</span>
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-200 border border-amber-500/30 flex items-center space-x-1">
                        <ClockIcon className="w-3.5 h-3.5 text-amber-300" />
                        <span>In Queue for Review</span>
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-white font-medium leading-relaxed bg-black/20 p-3 rounded-xl border border-white/10">
                  "{q.questionText}"
                </p>

                {/* Instructor Talking Points / Resolution if provided */}
                {q.instructorNotes && (
                  <div className="p-3 rounded-xl bg-indigo-950/60 border border-indigo-400/40 text-xs space-y-1">
                    <div className="flex items-center space-x-1.5 text-indigo-300 font-bold">
                      <AcademicCapIcon className="w-4 h-4" />
                      <span>
                        Instructor Class Note {q.instructorName ? `by ${q.instructorName}` : ''}:
                      </span>
                    </div>
                    <p className="text-white pl-5 font-normal">
                      {q.instructorNotes}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between text-[11px] text-blue-200/80 pt-1 border-t border-white/10">
                  <span>Targeted for: {q.targetClassWeek || 'Upcoming Classes Next Week'}</span>
                  <span>Submitted: {q.submittedAtStr || 'Recently'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Previous Weeks Archive (Collapsible) */}
      {pastWeekQueries.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-3xl p-5 shadow-lg">
          <button
            type="button"
            onClick={() => setShowPastWeeks(!showPastWeeks)}
            className="w-full flex items-center justify-between text-left text-sm font-bold text-white hover:text-indigo-200 transition"
          >
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-4 h-4 text-blue-300" />
              <span>Previous Weeks' Questions Archive ({pastWeekQueries.length})</span>
            </div>
            {showPastWeeks ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>

          {showPastWeeks && (
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
              {pastWeekQueries.map((q) => (
                <div
                  key={q.id}
                  className="p-3.5 rounded-xl bg-white/5 border border-white/10 text-xs space-y-1.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-blue-200">{q.categoryLabel || q.category}</span>
                    <span className="text-[10px] text-blue-300 font-medium px-2 py-0.5 rounded bg-white/10">
                      {formatMondayDisplayName(q.weekMondayDate || q.weekIdentifier)}
                    </span>
                  </div>
                  <p className="text-white">"{q.questionText}"</p>
                  {q.instructorNotes && (
                    <p className="text-indigo-300 text-[11px]">
                      <strong>Class Note:</strong> {q.instructorNotes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeeklyQueriesStudentTab;
